import { useState, useCallback, useEffect, useRef } from "react";
import { Github, Check, Loader2, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/ui/status-dot";
import { api } from "@/lib/api";

export function GitHubConnectWidget() {
  const [status, setStatus] = useState<"idle" | "waiting" | "connected" | "timeout">("idle");
  const [accountName, setAccountName] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Shared check that resolves the widget on success
  const checkAndResolve = useCallback(async () => {
    try {
      const result = await api.github.status();
      if (result.connected && result.installations.length > 0) {
        setAccountName(result.installations[0]?.account_login ?? null);
        setStatus("connected");
        return true;
      }
    } catch {
      // Ignore — keep waiting
    }
    return false;
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Check if already connected on mount — handles stale backend state
  useEffect(() => {
    checkAndResolve();
  }, [checkAndResolve]);

  // Listen for cross-tab localStorage signal from OAuth popup
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === "github-connected" && status === "waiting") {
        checkAndResolve();
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [status, checkAndResolve]);

  const startPolling = useCallback(() => {
    setStatus("waiting");
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes at 2s intervals

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(async () => {
      attempts++;
      const resolved = await checkAndResolve();
      if (resolved) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      if (attempts >= maxAttempts) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setStatus("timeout");
      }
    }, 2000);
  }, [checkAndResolve]);

  function handleConnect() {
    const popup = window.open(api.github.oauthUrl(), "github-oauth", "popup");
    if (!popup || popup.closed) {
      // Popup blocked — navigate in same tab; Home.tsx handles the return
      window.location.href = api.github.oauthUrl();
      return;
    }
    startPolling();
  }

  if (status === "connected") {
    return (
      <Card variant="success" padding="none" className="overflow-hidden max-w-md animate-slide-up">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-success/10">
            <Github size={16} className="text-daydra-success" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-daydra-text">GitHub connected</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <StatusDot status="connected" size="sm" />
              <span className="text-2xs text-daydra-dim">{accountName}</span>
            </div>
          </div>
          <Check size={18} className="text-daydra-success shrink-0" />
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="overflow-hidden max-w-md animate-slide-up">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent-ghost">
          <Github size={16} className="text-daydra-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-daydra-text">Connect GitHub</p>
          <p className="text-2xs text-daydra-muted mt-0.5">
            {status === "waiting"
              ? "Waiting for GitHub authorization..."
              : status === "timeout"
                ? "Connection timed out"
                : "Link your repositories to start building"}
          </p>
        </div>
        {status === "waiting" && (
          <Loader2 size={16} className="text-daydra-accent animate-spin shrink-0" />
        )}
        {status === "timeout" && (
          <AlertCircle size={16} className="text-daydra-error shrink-0" />
        )}
      </div>

      {status === "idle" && (
        <div className="flex gap-2 px-4 py-3 border-t border-daydra-border">
          <Button variant="default" size="sm" onClick={handleConnect}>
            Connect GitHub
          </Button>
        </div>
      )}

      {status === "waiting" && (
        <div className="flex gap-2 px-4 py-3 border-t border-daydra-border">
          <Button variant="secondary" size="sm" onClick={handleConnect}>
            Open GitHub again
          </Button>
        </div>
      )}

      {status === "timeout" && (
        <div className="flex gap-2 px-4 py-3 border-t border-daydra-border">
          <Button variant="default" size="sm" onClick={handleConnect}>
            Retry
          </Button>
        </div>
      )}
    </Card>
  );
}
